import axios from 'axios'
import * as httpAuthAdapter from '@/services/auth/adapters/httpAuthAdapter'

const adapter = httpAuthAdapter

const normalizeError = (error) => {
  // Check Axios errors FIRST — they have a string `code` like 'ERR_BAD_REQUEST'
  // which would falsely match the app-level code check below.
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const status = Number(error.response.status)
      const serverMessage = error.response?.data?.data || error.response?.data?.message || ''
      const fieldErrors = error.response?.data?.errors || null

      if (status === 401) {
        return {
          code: 'INVALID_CREDENTIALS',
          message: serverMessage || 'Invalid mobile number or password.',
          meta: { status }
        }
      }

      if (status === 404) {
        return {
          code: 'ACCOUNT_NOT_FOUND',
          message: serverMessage || 'Account not found. Please sign up first.',
          meta: { status }
        }
      }

      if (status === 409) {
        return {
          code: 'ACCOUNT_EXISTS',
          message: serverMessage || 'Account already exists. Please sign in.',
          meta: { status }
        }
      }

      if (status === 422) {
        // Prefer the first field-level error message if available,
        // otherwise fall back to the top-level API message.
        const firstFieldMsg = fieldErrors
          ? Object.values(fieldErrors).flat()[0]
          : ''

        return {
          code: 'VALIDATION_ERROR',
          message: firstFieldMsg || serverMessage || 'Invalid input. Please check your details.',
          meta: { status, fields: fieldErrors }
        }
      }

      return {
        code: 'NETWORK_ERROR',
        message: serverMessage || `Authentication API failed (${status}).`,
        meta: { status }
      }
    }

    return {
      code: 'NETWORK_ERROR',
      message: 'Authentication API is unreachable or timed out.',
      meta: null
    }
  }

  // App-level errors thrown by adapters (mock adapter, etc.)
  if (error?.code && typeof error.code === 'string') {
    return {
      code: error.code,
      message: error.message || 'Authentication request failed.',
      meta: error.meta || null
    }
  }

  return {
    code: 'UNKNOWN',
    message: String(error?.message || 'Unexpected authentication error.'),
    meta: null
  }
}

const execute = async (operation) => {
  try {
    return await operation()
  } catch (error) {
    throw normalizeError(error)
  }
}

// --- Public auth operations ---

export const login = ({ mobile, password }) =>
  execute(() => adapter.login({ mobile, password }))

export const signup = ({ mobile, fullName, email, password, passwordConfirmation, callingCode }) =>
  execute(() => adapter.signup({ mobile, fullName, email, password, passwordConfirmation, callingCode }))

export const validateMobile = ({ mobile, source, fullName, email, password, passwordConfirmation }) =>
  execute(() => adapter.validateMobile({ mobile, source, fullName, email, password, passwordConfirmation }))

export const sendOtp = ({ mobile }) =>
  execute(() => adapter.sendOtp({ mobile }))

export const verifyOtp = ({ mobile, otp, verifyId }) =>
  execute(() => adapter.verifyOtp({ mobile, otp, verifyId }))

export const forgotPassword = ({ mobile, otp, verifyId, password, passwordConfirmation }) =>
  execute(() => adapter.forgotPassword({ mobile, otp, verifyId, password, passwordConfirmation }))

// --- Protected auth operations (JWT required) ---

export const signout = () =>
  execute(() => adapter.signout())

export const fetchMe = () =>
  execute(() => adapter.fetchMe())

export const validateToken = ({ token, source }) =>
  execute(() => adapter.validateToken({ token, source }))
